import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface Toast {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
    action?: ToastAction;
}

interface ToastContextType {
    addToast: (message: string, type?: ToastType, duration?: number, action?: ToastAction) => void;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((message: string, type: ToastType = 'info', duration: number = 4000, action?: ToastAction) => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts((prev) => [...prev, { id, message, type, duration, action }]);

        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ addToast, removeToast }}>
            {children}
            <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={`
                            pointer-events-auto flex items-start gap-3 p-4 rounded-xl shadow-lg border animate-slide-in-right transition-all
                            ${toast.type === 'success' ? 'bg-white border-green-100 text-gray-800' : ''}
                            ${toast.type === 'error' ? 'bg-white border-red-100 text-gray-800' : ''}
                            ${toast.type === 'warning' ? 'bg-white border-orange-100 text-gray-800' : ''}
                            ${toast.type === 'info' ? 'bg-white border-blue-100 text-gray-800' : ''}
                        `}
                    >
                        <div className="flex-shrink-0 mt-0.5">
                            {toast.type === 'success' && <CheckCircle size={20} className="text-green-500" />}
                            {toast.type === 'error' && <AlertCircle size={20} className="text-red-500" />}
                            {toast.type === 'warning' && <AlertTriangle size={20} className="text-orange-500" />}
                            {toast.type === 'info' && <Info size={20} className="text-blue-500" />}
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium">{toast.message}</p>
                            {toast.action && (
                                <button
                                    onClick={() => { toast.action!.onClick(); removeToast(toast.id); }}
                                    className="mt-2 px-3 py-1.5 text-xs font-bold rounded-lg bg-primary text-white hover:opacity-90 transition-opacity"
                                >
                                    {toast.action.label}
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() => removeToast(toast.id)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>
            {/* Styles for animation - Assuming global CSS exists, but injecting simple style here just in case or relying on Tailwind classes if configured */}
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .animate-slide-in-right {
                    animation: slideInRight 0.3s ease-out forwards;
                }
            `}</style>
        </ToastContext.Provider>
    );
};
